import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { FeaturedButton, FeaturedBadge, FeaturedStats, useFeaturedReturn } from "../components/Featured";
import { JobCard } from "../components/JobCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Briefcase, Users, Plus, Loader2, Building2, FileText, MapPin, Eye, 
  CheckCircle, Clock, XCircle, Mail, Phone, MessageSquare, MessageCircle, Wrench, 
  CreditCard, Gift, AlertCircle, ArrowRight, Tag, Bell, User, ExternalLink,
  Layers, Upload, Image, X
} from "lucide-react";
import { SETTORI_AZIENDALI } from "../data/settoriSkills";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICINO_LOCATIONS = [
  "Airolo", "Arbedo-Castione", "Arogno", "Arzo", "Ascona", "Astano", "Balerna", "Bedano", 
  "Bedigliora", "Bellinzona", "Biasca", "Bioggio", "Bissone", "Blenio", "Bodio", "Bosco Gurin",
  "Breganzona", "Brione sopra Minusio", "Brissago", "Brusino Arsizio", "Cademario", "Cadempino", 
  "Cadenazzo", "Canobbio", "Capriasca", "Caslano", "Castel San Pietro", "Centovalli", "Chiasso", 
  "Claro", "Coldrerio", "Collina d'Oro", "Comano", "Croglio", "Cugnasco-Gerra", "Cureglia", "Curio",
  "Faido", "Gambarogno", "Giornico", "Gordola", "Grancia", "Gravesano", "Lamone", "Lavizzara",
  "Ligornetto", "Locarno", "Losone", "Lugano", "Lumino", "Maggia", "Manno", "Maroggia", 
  "Massagno", "Melano", "Melide", "Mendrisio", "Meride", "Mezzovico-Vira", "Minusio", "Monteceneri",
  "Monteggio", "Morbio Inferiore", "Morcote", "Muralto", "Neggio", "Novaggio", "Novazzano", 
  "Origlio", "Orselina", "Paradiso", "Personico", "Pollegio", "Ponte Capriasca", "Ponte Tresa",
  "Porza", "Prato Leventina", "Pregassona", "Pura", "Quinto", "Rivera", "Ronco sopra Ascona",
  "Rovio", "San Nazzaro", "Sant'Antonino", "Savosa", "Sementina", "Serravalle", "Sorengo", 
  "Stabio", "Tenero-Contra", "Terre di Pedemonte", "Torricella-Taverne", "Tremona", "Vacallo", 
  "Vernate", "Vezia", "Vico Morcote", "Viganello", "Remote"
];

// Settori lavorativi
const SECTORS = [
  { value: "", label: "Seleziona settore..." },
  ...SETTORI_AZIENDALI.map(s => ({ value: s, label: s }))
];

// Tipi di contratto disponibili
const CONTRACT_TYPES = [
  { value: "tempo_indeterminato", label: "Tempo indeterminato" },
  { value: "tempo_determinato", label: "Tempo determinato" },
  { value: "apprendistato", label: "Apprendistato" },
  { value: "stage", label: "Stage/Tirocinio" },
  { value: "contratto_progetto", label: "Contratto a progetto" },
  { value: "lavoro_temporaneo", label: "Lavoro temporaneo tramite agenzia" },
  { value: "freelance", label: "Freelance" },
  { value: "part_time", label: "Part-time" },
  { value: "full_time", label: "Full-time" },
  { value: "stagionale", label: "Stagionale" },
  { value: "a_chiamata", label: "A chiamata" },
];

// Tipi di inserzionista
const ADVERTISER_TYPES = [
  { value: "company", label: "Azienda" },
  { value: "agency", label: "Agenzia di lavoro" },
  { value: "private", label: "Privato" },
];

// Modalità di contatto
const CONTACT_PREFERENCES = [
  { value: "internal", label: "Messaggistica interna", icon: MessageSquare, description: "Solo attraverso il sistema di messaggistica dell'app" },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle, description: "Contatto tramite WhatsApp" },
  { value: "email", label: "Email", icon: Mail, description: "Mostra l'indirizzo email nell'annuncio" },
  { value: "phone", label: "Chiamata", icon: Phone, description: "Mostra il numero di telefono nell'annuncio" },
];

const statusLabels = { pending: "In attesa", reviewed: "Visionato", accepted: "Accettato", rejected: "Rifiutato" };
const statusColors = { pending: "bg-amber-100 text-amber-700", reviewed: "bg-blue-100 text-blue-700", accepted: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

export default function CompanyDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [companyServices, setCompanyServices] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Job Modal
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteJobId, setDeleteJobId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [profileViewed, setProfileViewed] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [uploadingJobImage, setUploadingJobImage] = useState(false);
  const [jobForm, setJobForm] = useState({ 
    title: "", 
    description: "", 
    location: "", 
    advertiser_type: "company",
    sector: "",
    contract_types: [],
    contact_preferences: ["internal"],
    image_url: "",
    private_first_name: "",
    private_last_name: "",
    private_phone: "",
    private_email: ""
  });
  
  // Service Modal States
  const [showServicePaywall, setShowServicePaywall] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceForm, setServiceForm] = useState({ title: "", description: "", tariffa: "", location: "", image_url: "", contact_methods: ["internal"] });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [serviceSessionId, setServiceSessionId] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [editingService, setEditingService] = useState(null);
  const [deleteServiceId, setDeleteServiceId] = useState(null);

  useFeaturedReturn(() => fetchData());

  useEffect(() => {
    fetchData();
    
    // Check if returning from payment
    const servicePayment = searchParams.get("service_payment");
    const sessionId = searchParams.get("session_id");
    
    if (servicePayment === "success" && sessionId) {
      setCheckingPayment(true);
      checkPaymentAndShowForm(sessionId);
    } else if (servicePayment === "cancelled") {
      toast.error("Pagamento annullato");
      // Clear params
      setSearchParams(new URLSearchParams());
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsRes, appsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/company/jobs`),
        axios.get(`${API}/company/applications`),
        axios.get(`${API}/company/my-services`).catch(() => ({ data: [] }))
      ]);
      setJobs(jobsRes.data);
      setApplications(appsRes.data);
      setCompanyServices(servicesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentAndShowForm = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/company/services/payment-status/${sessionId}`);
      if (res.data.can_create_service) {
        setServiceSessionId(sessionId);
        setShowServiceModal(true);
        toast.success("Pagamento confermato! Ora puoi inserire il tuo servizio.");
      } else {
        toast.error("Pagamento non ancora confermato. Riprova tra qualche istante.");
      }
    } catch (error) {
      toast.error("Errore nella verifica del pagamento");
    } finally {
      setCheckingPayment(false);
      // Clear URL params
      setSearchParams(new URLSearchParams());
    }
  };

  const openNewJobModal = () => {
    setEditingJob(null);
    setJobForm({ 
      title: "", 
      description: "", 
      location: "", 
      advertiser_type: "company",
      sector: "",
      contract_types: [],
      contact_preferences: ["internal"],
      private_first_name: "",
      private_last_name: "",
      private_phone: "",
      private_email: "",
      image_url: ""
    });
    setShowJobModal(true);
  };

  const openEditJobModal = (job) => {
    setEditingJob(job);
    setJobForm({ 
      title: job.title, 
      description: job.description, 
      location: job.location, 
      advertiser_type: job.advertiser_type || "company",
      sector: job.sector || "",
      contract_types: job.contract_types || [],
      contact_preferences: job.contact_preferences || ["internal"],
      private_first_name: job.private_first_name || "",
      private_last_name: job.private_last_name || "",
      private_phone: job.private_phone || "",
      private_email: job.private_email || "",
      image_url: job.image_url || ""
    });
    setShowJobModal(true);
  };

  const handleSaveJob = async () => {
    // Validazione base
    if (!jobForm.title || !jobForm.description || !jobForm.location || !jobForm.advertiser_type) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    // Validazione settore
    if (!jobForm.sector) {
      toast.error("Seleziona un settore");
      return;
    }
    
    // Validazione tipo contratto
    if (!jobForm.contract_types || jobForm.contract_types.length === 0) {
      toast.error("Seleziona almeno un tipo di contratto");
      return;
    }
    
    // Validazione modalità contatto
    if (!jobForm.contact_preferences || jobForm.contact_preferences.length === 0) {
      toast.error("Seleziona almeno una modalità di contatto");
      return;
    }
    
    // Validazione per privati
    if (jobForm.advertiser_type === "private") {
      if (!jobForm.private_first_name) {
        toast.error("Il nome è obbligatorio per annunci privati");
        return;
      }
      if (jobForm.contact_preferences.includes("phone") && !jobForm.private_phone) {
        toast.error("Inserisci il numero di telefono");
        return;
      }
      if (jobForm.contact_preferences.includes("email") && !jobForm.private_email) {
        toast.error("Inserisci l'indirizzo email");
        return;
      }
    }

    setSaving(true);
    try {
      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob.id}`, jobForm);
        toast.success("Annuncio aggiornato!");
      } else {
        await axios.post(`${API}/jobs`, jobForm);
        toast.success("Annuncio pubblicato!");
      }
      setShowJobModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella pubblicazione");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!deleteJobId) return;
    try {
      await axios.delete(`${API}/jobs/${deleteJobId}`);
      toast.success("Annuncio eliminato");
      setDeleteJobId(null);
      fetchData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    const contactMethods = service.contact_methods || (service.contact_method ? [service.contact_method] : ["internal"]);
    setServiceForm({
      title: service.title,
      description: service.description,
      tariffa: service.tariffa || "",
      location: service.location || "",
      image_url: service.image_url || "",
      contact_methods: contactMethods
    });
    setShowServiceModal(true);
  };

  const handleDeleteService = async () => {
    if (!deleteServiceId) return;
    try {
      await axios.delete(`${API}/company/services/${deleteServiceId}`);
      toast.success("Servizio eliminato");
      setDeleteServiceId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleUpdateService = async () => {
    if (!serviceForm.title || !serviceForm.description || !serviceForm.location) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (!serviceForm.contact_methods || serviceForm.contact_methods.length === 0) {
      toast.error("Seleziona almeno un metodo di contatto");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/company/services/${editingService.id}`, serviceForm);
      toast.success("Servizio aggiornato");
      setShowServiceModal(false);
      setEditingService(null);
      setServiceForm({ title: "", description: "", tariffa: "", location: "", image_url: "", contact_methods: ["internal"] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const handleJobImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error("Seleziona un file immagine");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande. Max 5MB");
      return;
    }
    setUploadingJobImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/jobs/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setJobForm(prev => ({ ...prev, image_url: res.data.url }));
      toast.success("Immagine caricata");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploadingJobImage(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error("Seleziona un file immagine");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande. Max 5MB");
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setServiceForm(prev => ({ ...prev, image_url: res.data.url }));
      toast.success("Immagine caricata");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateApplicationStatus = async (appId, newStatus) => {
    if (updatingStatus) return; // Previeni click multipli
    
    const oldStatus = selectedApplication?.status;
    const statusLabels = { pending: "In attesa", reviewed: "Visionato", accepted: "Accettata", rejected: "Rifiutata" };
    
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/applications/${appId}/status?status=${newStatus}`);
      
      // Aggiorna lo stato locale immediatamente
      setSelectedApplication(prev => prev ? { ...prev, status: newStatus } : null);
      setApplications(prev => prev.map(app => 
        app.id === appId ? { ...app, status: newStatus } : app
      ));
      
      // Mostra messaggio di conferma
      if (oldStatus && oldStatus !== newStatus) {
        toast.success(`Stato cambiato: ${statusLabels[oldStatus]} → ${statusLabels[newStatus]}`);
      } else {
        toast.success(`Candidatura ${statusLabels[newStatus]}`);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento dello stato");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Service functions
  const handleOpenServicePaywall = () => {
    setShowServicePaywall(true);
  };

  const handleStartServicePayment = async () => {
    setProcessingPayment(true);
    try {
      const res = await axios.post(`${API}/company/services/checkout`, {
        promo_code: promoCode || null
      });
      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel processare il pagamento");
      setProcessingPayment(false);
    }
  };

  const handleSaveService = async () => {
    if (!serviceForm.title || !serviceForm.description || !serviceForm.location) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (!serviceForm.contact_methods || serviceForm.contact_methods.length === 0) {
      toast.error("Seleziona almeno un metodo di contatto");
      return;
    }

    if (!serviceSessionId) {
      toast.error("Sessione di pagamento non valida");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/company/services?session_id=${serviceSessionId}`, serviceForm);
      toast.success("Servizio pubblicato con successo!");
      setShowServiceModal(false);
      setServiceSessionId(null);
      setServiceForm({ title: "", description: "", tariffa: "", location: "", image_url: "", contact_methods: ["internal"] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella pubblicazione");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("it-CH", { day: "numeric", month: "short", year: "numeric" });

  const getContactIcon = (method) => {
    switch (method) {
      case "email": return <Mail className="w-4 h-4" />;
      case "phone": return <Phone className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  // Check if in launch period (90 giorni dal 16/04/2026)
  const isLaunchPeriod = () => {
    const launchDate = new Date("2026-04-16T00:00:00");
    const promoEnd = new Date(launchDate.getTime() + 90 * 24 * 60 * 60 * 1000);
    return new Date() < promoEnd;
  };

  const servicePrice = 48.00;  // Prezzo pieno, sconto con codice LANCIO50
  const originalPrice = 48.00;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="company-dashboard">
      <Navbar />
      
      {/* Checking payment overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#004E64]" />
            <p className="text-lg font-medium">Verifica pagamento in corso...</p>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{user?.company_name || user?.name}</h1>
            <p className="text-slate-600 mt-1">Gestisci i tuoi annunci, servizi e candidature</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={openNewJobModal} className="bg-[#DA291C] hover:bg-[#B91C1C] text-white" data-testid="new-job-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo annuncio
            </Button>
            <Button onClick={handleOpenServicePaywall} variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50" data-testid="new-service-btn">
              <Wrench className="w-4 h-4 mr-2" /> Inserisci Servizio
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#004E64]" /> Panoramica
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div 
                    className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => document.getElementById('jobs-section')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <div className="text-2xl font-bold text-[#004E64]">{jobs.length}</div>
                    <div className="text-xs text-slate-500">Annunci</div>
                  </div>
                  <div 
                    className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <div className="text-2xl font-bold text-emerald-600">{companyServices.length}</div>
                    <div className="text-xs text-slate-500">Servizi</div>
                  </div>
                  <div 
                    className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => document.getElementById('applications-section')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <div className="text-2xl font-bold text-[#DA291C]">{applications.length}</div>
                    <div className="text-xs text-slate-500">Candidature</div>
                    {applications.filter(a => a.status === "pending").length > 0 && (
                      <Badge className="mt-1 bg-[#DA291C] text-white text-xs">
                        {applications.filter(a => a.status === "pending").length} nuove
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="applications-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Candidature recenti</span>
                  {applications.filter(a => a.status === "pending").length > 0 && (
                    <Bell className="w-5 h-5 text-[#DA291C] animate-pulse" />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {applications.length > 0 ? (
                  <div className="space-y-3">
                    {applications.slice(0, 5).map((app) => (
                      <div key={app.id} className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-slate-100 ${app.status === "pending" ? "bg-blue-50 border border-blue-200" : "bg-slate-50"}`} onClick={() => setSelectedApplication(app)}>
                        <div>
                          <p className="font-medium text-slate-900 flex items-center gap-2">
                            {app.candidate_name}
                            {app.status === "pending" && <Bell className="w-3 h-3 text-[#DA291C]" />}
                          </p>
                          <p className="text-sm text-slate-500">{app.job_title}</p>
                        </div>
                        <Badge className={statusColors[app.status]}>{statusLabels[app.status]}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Nessuna candidatura</p>
                )}
              </CardContent>
            </Card>

            {/* Company Services */}
            {companyServices.length > 0 && (
              <Card id="services-section">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-emerald-600" /> I tuoi Servizi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {companyServices.map((service) => (
                      <div key={service.id} className="p-3 bg-emerald-50 rounded-lg">
                        <p className="font-medium text-slate-900">{service.title}</p>
                        <FeaturedBadge item={service} className="mt-1" />
                        <p className="text-sm text-slate-500 truncate">{service.description}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400">{formatDate(service.created_at)}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs flex items-center gap-1 text-slate-500">
                              <Eye className="w-3 h-3" /> {service.views || 0}
                            </span>
                            <button
                              onClick={() => handleEditService(service)}
                              className="text-xs text-slate-600 hover:text-[#004E64] hover:underline"
                            >
                              Modifica
                            </button>
                            <button
                              onClick={() => setDeleteServiceId(service.id)}
                              className="text-xs text-red-600 hover:text-red-700 hover:underline"
                            >
                              Elimina
                            </button>
                          </div>
                        </div>
                        <div className="mt-2"><FeaturedButton kind="service" item={service} /></div>
                        <FeaturedStats item={service} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Jobs List */}
          <div className="lg:col-span-2">
            <Card id="jobs-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#004E64]" /> I tuoi Annunci
                </CardTitle>
                <CardDescription>Gestisci le posizioni aperte</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : jobs.length > 0 ? (
                  <div className="space-y-4">
                    {jobs.map((job) => (
                      <div key={job.id} className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                          <div>
                            <h3 className="font-semibold text-slate-900">{job.title}</h3>
                            <FeaturedBadge item={job} className="mt-1" />
                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                              <MapPin className="w-4 h-4" /> {job.location}
                              <span className="mx-1">•</span>
                              <Eye className="w-4 h-4" /> {job.views || 0} visite
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {job.is_active !== false && <FeaturedButton kind="job" item={job} />}
                            <Button size="sm" variant="outline" onClick={() => openEditJobModal(job)}>Modifica</Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteJobId(job.id)}>Elimina</Button>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{job.description}</p>
                        <p className="text-xs text-slate-400 mt-2">Pubblicato il {formatDate(job.created_at)}</p>
                        <FeaturedStats item={job} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-700 mb-1">Nessun annuncio</h3>
                    <p className="text-slate-500 text-sm mb-4">Pubblica il tuo primo annuncio di lavoro</p>
                    <Button onClick={openNewJobModal} className="bg-[#DA291C] hover:bg-[#B91C1C] text-white">
                      <Plus className="w-4 h-4 mr-2" /> Crea annuncio
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Job Modal - Form Inserimento Annuncio */}
      <Dialog open={showJobModal} onOpenChange={setShowJobModal}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-black text-xl">{editingJob ? "Modifica annuncio" : "Nuovo annuncio di lavoro"}</DialogTitle>
          </DialogHeader>
          
          {/* Messaggio informativo sul nome pubblico */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-800">Nome visibile nell'annuncio</p>
              <p className="text-blue-700 mt-0.5">
                {jobForm.advertiser_type === "private" 
                  ? jobForm.private_first_name 
                    ? `Il tuo annuncio sarà pubblicato con il nome: "${jobForm.private_first_name}"`
                    : "Inserisci il tuo nome nei campi sottostanti"
                  : `Il tuo annuncio sarà pubblicato con il nome: "${user?.company_name || user?.name || 'La tua azienda'}"`
                }
              </p>
            </div>
          </div>
          
          <div className="grid gap-5 py-4">
            {/* Tipo inserzionista */}
            <div>
              <Label className="text-black font-medium">Chi pubblica l'annuncio *</Label>
              <Select value={jobForm.advertiser_type} onValueChange={(v) => setJobForm({...jobForm, advertiser_type: v})}>
                <SelectTrigger className="mt-1.5 bg-white border-slate-300 text-black">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {ADVERTISER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-black">{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Campi per Privato */}
            {jobForm.advertiser_type === "private" && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-4 border border-slate-200">
                <p className="text-sm font-medium text-slate-700">Dati del privato</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-black font-medium">Nome *</Label>
                    <Input 
                      value={jobForm.private_first_name} 
                      onChange={(e) => setJobForm({...jobForm, private_first_name: e.target.value})} 
                      placeholder="Mario"
                      className="mt-1.5 bg-white border-slate-300 text-black" 
                    />
                    <p className="text-xs text-slate-500 mt-1">Visibile nell'annuncio</p>
                  </div>
                  <div>
                    <Label className="text-black font-medium">Cognome</Label>
                    <Input 
                      value={jobForm.private_last_name} 
                      onChange={(e) => setJobForm({...jobForm, private_last_name: e.target.value})} 
                      placeholder="Rossi (opzionale)"
                      className="mt-1.5 bg-white border-slate-300 text-black" 
                    />
                    <p className="text-xs text-slate-500 mt-1">Non visibile pubblicamente</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-black font-medium">Telefono</Label>
                    <Input 
                      value={jobForm.private_phone} 
                      onChange={(e) => setJobForm({...jobForm, private_phone: e.target.value})} 
                      placeholder="+41 79 123 4567"
                      className="mt-1.5 bg-white border-slate-300 text-black" 
                    />
                  </div>
                  <div>
                    <Label className="text-black font-medium">Email</Label>
                    <Input 
                      type="email"
                      value={jobForm.private_email} 
                      onChange={(e) => setJobForm({...jobForm, private_email: e.target.value})} 
                      placeholder="mario@email.ch"
                      className="mt-1.5 bg-white border-slate-300 text-black" 
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Titolo posizione */}
            <div>
              <Label className="text-black font-medium">Titolo posizione *</Label>
              <Input 
                value={jobForm.title} 
                onChange={(e) => setJobForm({...jobForm, title: e.target.value})} 
                placeholder="es. Piastrellista con esperienza" 
                className="mt-1.5 bg-white border-slate-300 text-black placeholder:text-slate-400 focus:border-[#004E64] focus:ring-[#004E64]" 
              />
            </div>

            {/* Luogo */}
            <div>
              <Label className="text-black font-medium">Luogo *</Label>
              <Select value={jobForm.location} onValueChange={(v) => setJobForm({...jobForm, location: v})}>
                <SelectTrigger className="mt-1.5 bg-white border-slate-300 text-black">
                  <SelectValue placeholder="Seleziona comune" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {TICINO_LOCATIONS.map((loc) => <SelectItem key={loc} value={loc} className="text-black">{loc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Settore */}
            <div>
              <Label className="text-black font-medium">Settore *</Label>
              <Select value={jobForm.sector} onValueChange={(v) => setJobForm({...jobForm, sector: v})}>
                <SelectTrigger className="mt-1.5 bg-white border-slate-300 text-black">
                  <Layers className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Seleziona settore..." />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {SECTORS.filter(s => s.value).map((sector) => (
                    <SelectItem key={sector.value} value={sector.value} className="text-black">{sector.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tipo di contratto - NUOVO */}
            <div>
              <Label className="text-black font-medium">Tipo di contratto * <span className="text-slate-500 font-normal">(puoi selezionarne più di uno)</span></Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {CONTRACT_TYPES.map((type) => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`contract-${type.value}`}
                      checked={jobForm.contract_types.includes(type.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setJobForm({...jobForm, contract_types: [...jobForm.contract_types, type.value]});
                        } else {
                          setJobForm({...jobForm, contract_types: jobForm.contract_types.filter(t => t !== type.value)});
                        }
                      }}
                    />
                    <label htmlFor={`contract-${type.value}`} className="text-sm text-slate-700 cursor-pointer">{type.label}</label>
                  </div>
                ))}
              </div>
              {jobForm.contract_types.length > 0 && (
                <p className="text-xs text-emerald-600 mt-2">Selezionati: {jobForm.contract_types.map(t => CONTRACT_TYPES.find(c => c.value === t)?.label).join(", ")}</p>
              )}
            </div>

            {/* Descrizione */}
            <div>
              <Label className="text-black font-medium">Descrizione *</Label>
              <Textarea 
                value={jobForm.description} 
                onChange={(e) => setJobForm({...jobForm, description: e.target.value})} 
                placeholder="Descrivi la posizione, requisiti, mansioni..." 
                rows={5} 
                className="mt-1.5 bg-white border-slate-300 text-black placeholder:text-slate-400 focus:border-[#004E64] focus:ring-[#004E64]" 
              />
            </div>

            {/* Immagine annuncio */}
            <div>
              <Label className="text-black font-medium">Immagine Annuncio (opzionale)</Label>
              <p className="text-xs text-slate-500 mb-2">Aggiungi una foto o il logo per rendere l'annuncio più visibile (max 5MB)</p>
              {jobForm.image_url ? (
                <div className="relative mt-2">
                  <img
                    src={jobForm.image_url.startsWith('http') ? jobForm.image_url : `${process.env.REACT_APP_BACKEND_URL}${jobForm.image_url}`}
                    alt="Anteprima"
                    className="w-full h-40 object-cover rounded-lg border"
                    data-testid="job-image-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setJobForm({...jobForm, image_url: ""})}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    data-testid="job-image-remove-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#004E64] hover:bg-slate-50 transition-colors">
                  {uploadingJobImage ? (
                    <Loader2 className="w-8 h-8 text-[#004E64] animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Clicca per caricare</p>
                      <p className="text-xs text-slate-400">JPG, PNG, WebP (max 5MB)</p>
                    </>
                  )}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleJobImageUpload}
                    disabled={uploadingJobImage}
                    data-testid="job-image-input"
                  />
                </label>
              )}
            </div>

            {/* Modalità di contatto - NUOVO */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <Label className="text-black font-medium">Come preferisci essere contattato * <span className="text-slate-500 font-normal">(seleziona almeno uno)</span></Label>
              <div className="mt-3 space-y-3">
                {CONTACT_PREFERENCES.map((pref) => {
                  const Icon = pref.icon;
                  return (
                    <div key={pref.value} className="flex items-start space-x-3">
                      <Checkbox 
                        id={`contact-${pref.value}`}
                        checked={jobForm.contact_preferences.includes(pref.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setJobForm({...jobForm, contact_preferences: [...jobForm.contact_preferences, pref.value]});
                          } else {
                            setJobForm({...jobForm, contact_preferences: jobForm.contact_preferences.filter(p => p !== pref.value)});
                          }
                        }}
                      />
                      <div className="flex-1">
                        <label htmlFor={`contact-${pref.value}`} className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {pref.label}
                        </label>
                        <p className="text-xs text-slate-500">{pref.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {jobForm.contact_preferences.includes("phone") && jobForm.advertiser_type !== "private" && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Il telefono associato al tuo account verrà mostrato
                </p>
              )}
              {jobForm.contact_preferences.includes("email") && jobForm.advertiser_type !== "private" && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> L'email associata al tuo account verrà mostrata
                </p>
              )}
              {jobForm.contact_preferences.includes("whatsapp") && jobForm.advertiser_type !== "private" && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Il numero WhatsApp associato al tuo account verrà mostrato
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowJobModal(false)} className="border-slate-300 text-slate-700 hover:bg-slate-100">
              Annulla
            </Button>
            <Button onClick={handleSaveJob} disabled={saving} className="bg-[#DA291C] hover:bg-[#B91C1C] text-white font-semibold">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingJob ? "Salva modifiche" : "Pubblica annuncio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Paywall Modal */}
      <Dialog open={showServicePaywall} onOpenChange={setShowServicePaywall}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-black text-xl flex items-center gap-2">
              <Wrench className="w-6 h-6 text-emerald-600" />
              Pubblica un Servizio
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Espandi la tua visibilità offrendo anche servizi
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Annual Badge - Primary Focus */}
            <div className="bg-emerald-700 text-white text-lg font-bold px-5 py-3 rounded-xl text-center mb-4">
              ABBONAMENTO ANNUALE
            </div>
            
            {/* Duration Highlight */}
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-center mb-4">
              <p className="text-emerald-800 font-bold text-xl">Copertura per 365 giorni</p>
              <p className="text-emerald-600 text-sm mt-1">Meno di 4 CHF al mese</p>
            </div>
            
            {/* Price - Secondary */}
            <div className="text-center mb-4">
              <span className="text-2xl font-semibold text-slate-500">CHF {servicePrice}</span>
              <span className="text-sm text-slate-400 ml-1">/ anno</span>
            </div>
            
            {/* Promo Code Banner */}
            <p className="text-center text-sm text-amber-600 font-medium bg-amber-50 py-2 px-3 rounded-lg mb-4">
              Usa codice LANCIO50 per -50%
            </p>

            {/* Benefits */}
            <div className="space-y-2 text-sm text-slate-700 mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Il servizio resta online per sempre</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Visibile nella sezione "Offro Servizi"</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Contatti tramite chat interna</span>
              </div>
            </div>

            {/* Info */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg text-sm mb-4">
              <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-600">
                Dopo il pagamento potrai inserire tutti i dettagli del tuo servizio.
              </p>
            </div>

            {/* Codice Sconto */}
            <div>
              <Label htmlFor="promo-code" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Tag className="w-4 h-4" /> Codice Sconto (opzionale)
              </Label>
              <Input
                id="promo-code"
                type="text"
                placeholder="Inserisci codice sconto"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="mt-1.5 bg-white border-slate-300 text-black placeholder:text-slate-400"
              />
              <p className="text-xs text-slate-500 mt-1">Il codice verrà applicato automaticamente</p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowServicePaywall(false)} className="border-slate-300">
              Annulla
            </Button>
            <Button 
              onClick={handleStartServicePayment} 
              disabled={processingPayment}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              {processingPayment ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Elaborazione...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> Procedi al pagamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Form Modal (after payment or editing) */}
      <Dialog open={showServiceModal} onOpenChange={(open) => {
        if (!open && serviceSessionId && !editingService) {
          // Warn before closing only if new service with payment
          if (window.confirm("Sei sicuro? Perderai il pagamento effettuato.")) {
            setShowServiceModal(false);
            setServiceSessionId(null);
          }
        } else {
          setShowServiceModal(open);
          if (!open) {
            setEditingService(null);
            setServiceForm({ title: "", description: "", tariffa: "", location: "", image_url: "", contact_methods: ["internal"] });
          }
        }
      }}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-black text-xl flex items-center gap-2">
              {editingService ? (
                <><Wrench className="w-6 h-6 text-emerald-600" /> Modifica Servizio</>
              ) : (
                <><CheckCircle className="w-6 h-6 text-emerald-600" /> Inserisci il tuo Servizio</>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {editingService ? "Modifica i dettagli del servizio." : "Pagamento confermato! Completa i dettagli del servizio."}
            </DialogDescription>
          </DialogHeader>
          
          {/* Messaggio informativo sul nome pubblico */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-800">Nome visibile nell'annuncio</p>
              <p className="text-blue-700 mt-0.5">
                Il tuo servizio sarà pubblicato con il nome: "{user?.company_name || user?.name || 'Il tuo nome'}"
              </p>
            </div>
          </div>
          
          <div className="grid gap-5 py-4">
            <div>
              <Label className="text-black font-medium">Titolo servizio *</Label>
              <Input 
                value={serviceForm.title} 
                onChange={(e) => setServiceForm({...serviceForm, title: e.target.value})} 
                placeholder="es. Consulenza fiscale per PMI" 
                className="mt-1.5 bg-white border-slate-300 text-black placeholder:text-slate-400" 
              />
            </div>
            
            <div>
              <Label className="text-black font-medium">Descrizione *</Label>
              <Textarea 
                value={serviceForm.description} 
                onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})} 
                placeholder="Descrivi il servizio offerto, competenze, disponibilità..." 
                rows={5} 
                className="mt-1.5 bg-white border-slate-300 text-black placeholder:text-slate-400" 
              />
            </div>
            
            <div>
              <Label className="text-black font-medium">Tariffa (opzionale)</Label>
              <Input 
                value={serviceForm.tariffa} 
                onChange={(e) => setServiceForm({...serviceForm, tariffa: e.target.value})} 
                placeholder="es. CHF 100/ora, A progetto, Su richiesta" 
                className="mt-1.5 bg-white border-slate-300 text-black placeholder:text-slate-400" 
              />
            </div>

            <div>
              <Label className="text-black font-medium">Località *</Label>
              <Select value={serviceForm.location} onValueChange={(v) => setServiceForm({...serviceForm, location: v})}>
                <SelectTrigger className="mt-1.5 bg-white border-slate-300 text-black">
                  <SelectValue placeholder="Seleziona località" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {TICINO_LOCATIONS.map((loc) => (
                    <SelectItem key={loc} value={loc} className="text-black">{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-black font-medium">Immagine Servizio (opzionale)</Label>
              <p className="text-xs text-slate-500 mb-2">Carica un'immagine rappresentativa del tuo servizio (max 5MB)</p>
              
              {serviceForm.image_url ? (
                <div className="relative mt-2">
                  <img 
                    src={serviceForm.image_url.startsWith('http') 
                      ? serviceForm.image_url 
                      : `${process.env.REACT_APP_BACKEND_URL}${serviceForm.image_url}`
                    } 
                    alt="Preview" 
                    className="w-full h-40 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={() => setServiceForm({...serviceForm, image_url: ""})}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {uploadingImage ? (
                      <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        <p className="text-sm text-slate-500">Clicca per caricare</p>
                        <p className="text-xs text-slate-400">JPG, PNG, WebP (max 5MB)</p>
                      </>
                    )}
                  </div>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </label>
              )}
            </div>
            
            <div>
              <Label className="text-black font-medium">Metodo di contatto preferito</Label>
              <p className="text-xs text-slate-500 mb-2">Seleziona almeno un metodo</p>
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200 space-y-3">
                {CONTACT_PREFERENCES.map((pref) => {
                  const Icon = pref.icon;
                  return (
                    <div key={pref.value} className="flex items-start space-x-3">
                      <Checkbox 
                        id={`service-contact-${pref.value}`}
                        checked={serviceForm.contact_methods.includes(pref.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setServiceForm({...serviceForm, contact_methods: [...serviceForm.contact_methods, pref.value]});
                          } else {
                            setServiceForm({...serviceForm, contact_methods: serviceForm.contact_methods.filter(m => m !== pref.value)});
                          }
                        }}
                      />
                      <div className="flex-1">
                        <label htmlFor={`service-contact-${pref.value}`} className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {pref.label}
                        </label>
                        <p className="text-xs text-slate-500">{pref.description}</p>
                      </div>
                    </div>
                  );
                })}
                {serviceForm.contact_methods.includes("phone") && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Il telefono associato al tuo account verrà mostrato
                  </p>
                )}
                {serviceForm.contact_methods.includes("email") && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> L'email associata al tuo account verrà mostrata
                  </p>
                )}
                {serviceForm.contact_methods.includes("whatsapp") && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Il numero WhatsApp associato al tuo account verrà mostrato
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              onClick={editingService ? handleUpdateService : handleSaveService} 
              disabled={saving}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editingService ? "Aggiornamento..." : "Pubblicazione..."}</>
              ) : (
                <>{editingService ? "Salva modifiche" : "Pubblica servizio"} <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Detail Modal */}
      <Dialog open={!!selectedApplication} onOpenChange={(open) => {
        if (!open) {
          setSelectedApplication(null);
          setProfileViewed(false);
        }
      }}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          {selectedApplication && (
            <>
              <DialogHeader>
                <DialogTitle className="text-black text-xl">Candidatura di {selectedApplication.candidate_name}</DialogTitle>
                <DialogDescription className="text-base">Per: {selectedApplication.job_title}</DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-5">
                {/* Messaggio del candidato - SEZIONE PROMINENTE */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-[#004E64]" />
                    <p className="font-semibold text-slate-800">Messaggio del candidato:</p>
                  </div>
                  {selectedApplication.message ? (
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selectedApplication.message}</p>
                  ) : (
                    <p className="text-slate-400 italic">Il candidato non ha inserito un messaggio di presentazione.</p>
                  )}
                </div>

                {/* Info candidato quick view */}
                {(selectedApplication.candidate_comune || selectedApplication.candidate_bio) && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      {selectedApplication.candidate_comune && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> {selectedApplication.candidate_comune}
                        </span>
                      )}
                    </div>
                    {selectedApplication.candidate_bio && (
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{selectedApplication.candidate_bio}</p>
                    )}
                  </div>
                )}

                {/* Pulsante Visualizza Profilo Completo */}
                <Link 
                  to={`/candidates/${selectedApplication.candidate_id}`}
                  onClick={() => setProfileViewed(true)}
                  className="block"
                >
                  <Button 
                    variant="outline" 
                    className="w-full h-12 text-base border-2 border-[#004E64] text-[#004E64] hover:bg-[#004E64] hover:text-white transition-all"
                    data-testid="view-candidate-profile-btn"
                  >
                    <User className="w-5 h-5 mr-2" />
                    Visualizza profilo completo di {selectedApplication.candidate_name}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>

                {/* Avviso se profilo non visualizzato - solo informativo */}
                {!profileViewed && selectedApplication.status === "pending" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Ti consigliamo di visualizzare il profilo completo del candidato prima di aggiornare lo stato.
                    </p>
                  </div>
                )}
                
                {/* Aggiorna stato - SEMPRE CLICCABILI */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-3">Aggiorna stato:</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      size="sm" 
                      variant={selectedApplication.status === "reviewed" ? "default" : "outline"} 
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, "reviewed")} 
                      className={`transition-all ${selectedApplication.status === "reviewed" ? "bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-300" : "hover:border-blue-400"}`}
                      disabled={updatingStatus}
                      data-testid="status-btn-reviewed"
                    >
                      {updatingStatus ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
                      Visionato
                      {selectedApplication.status === "reviewed" && <CheckCircle className="w-3 h-3 ml-1" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant={selectedApplication.status === "accepted" ? "default" : "outline"} 
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, "accepted")} 
                      className={`transition-all ${selectedApplication.status === "accepted" ? "bg-emerald-600 hover:bg-emerald-700 text-white ring-2 ring-emerald-300" : "hover:border-emerald-400"}`}
                      disabled={updatingStatus}
                      data-testid="status-btn-accepted"
                    >
                      {updatingStatus ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                      Accetta
                      {selectedApplication.status === "accepted" && <CheckCircle className="w-3 h-3 ml-1" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant={selectedApplication.status === "rejected" ? "default" : "outline"} 
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, "rejected")} 
                      className={`transition-all ${selectedApplication.status === "rejected" ? "bg-red-600 hover:bg-red-700 text-white ring-2 ring-red-300" : "hover:border-red-400"}`}
                      disabled={updatingStatus}
                      data-testid="status-btn-rejected"
                    >
                      {updatingStatus ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
                      Rifiuta
                      {selectedApplication.status === "rejected" && <CheckCircle className="w-3 h-3 ml-1" />}
                    </Button>
                  </div>
                  {selectedApplication.status !== "pending" && (
                    <p className="text-xs text-slate-500 mt-2">
                      Stato attuale: <span className="font-medium">{statusLabels[selectedApplication.status]}</span> - Puoi modificarlo in qualsiasi momento
                    </p>
                  )}
                </div>
                
                {/* Contatta candidato */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-2">Contatta candidato:</p>
                  <Link to={`/messages?to=${selectedApplication.candidate_id}&name=${encodeURIComponent(selectedApplication.candidate_name)}`}>
                    <Button variant="outline" className="w-full">
                      <MessageSquare className="w-4 h-4 mr-2" /> Invia messaggio
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Job Confirmation */}
      <AlertDialog open={!!deleteJobId} onOpenChange={() => setDeleteJobId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Eliminare l'annuncio?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-300">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteJob} className="bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Service Confirmation */}
      <AlertDialog open={!!deleteServiceId} onOpenChange={() => setDeleteServiceId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Eliminare il servizio?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-300">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteService} className="bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
